import { z } from 'zod';
import { esquemaConsultaPagina } from './comun.js';
/**
 * Contratos del panel de proveedor.
 *
 * Dos reglas gobiernan estos esquemas, y las dos vienen de agujeros reales de
 * la v1:
 *
 *  1. **El `proveedorId` NO viaja en el cuerpo.** Se resuelve con
 *     `proveedorObjetivo`, que lo saca del token y solo acepta un
 *     `?proveedorId=` explícito cuando quien pide es del equipo. En la v1 el
 *     dueño del producto se tomaba del formulario, así que un proveedor podía
 *     publicar a nombre de la competencia. Los esquemas son `strict()`: mandar
 *     el campo es un 400, no un campo ignorado en silencio.
 *  2. **El margen no se acepta de un proveedor.** El precio de venta lo fija
 *     Compra Norte. `margenOverride` solo existe en el esquema de edición, y
 *     el módulo comprueba que quien lo manda sea del equipo.
 *
 * La única excepción a la regla 1 es la importación masiva, cuyo cuerpo lleva
 * un `proveedorId` opcional porque el equipo carga catálogos por cuenta de un
 * proveedor. Ese campo también pasa por `proveedorObjetivo`, así que un
 * proveedor que ponga el de otro recibe 404.
 */
/**
 * Tope de filas por importación.
 *
 * No es un número redondo por gusto: la carga entera corre en UNA transacción,
 * y cada fila puede además emitir un movimiento de stock con su lectura
 * bloqueante. Cinco mil filas cubren el catálogo completo de cualquier
 * proveedor de la cartera y mantienen la transacción en un largo que la base
 * sostiene. Un archivo mayor se parte en dos cargas.
 */
export const TOPE_FILAS_IMPORTACION = 5000;
/** Largo máximo del SKU: es el de la columna. */
export const LARGO_MAXIMO_SKU = 60;
/**
 * Peso y dimensiones: obligatorios y mayores que cero, SIEMPRE.
 *
 * Es la regla de negocio central del formulario de la v1 y la razón está en el
 * motor de flete: el peso facturable compara el peso real con el volumétrico
 * (largo × ancho × alto / divisor), así que sin las cuatro cifras no hay tarifa
 * que aplicar y el producto no se puede cotizar. Los mensajes lo dicen con esas
 * palabras porque los lee el proveedor que está cargando el catálogo.
 */
function medida(campo, unidad) {
    return z
        .number({
        required_error: `Falta ${campo}: sin peso ni dimensiones no se puede cotizar el flete`,
        invalid_type_error: `${campo} debe ser un número en ${unidad}: sin peso ni dimensiones no se puede cotizar el flete`,
    })
        .positive(`${campo} debe ser mayor que cero: sin peso ni dimensiones no se puede cotizar el flete`)
        .max(100_000, `${campo} es demasiado grande`);
}
const pesoKg = medida('el peso', 'kg');
const largoCm = medida('el largo', 'cm');
const anchoCm = medida('el ancho', 'cm');
const altoCm = medida('el alto', 'cm');
const costoNeto = z
    .number({ required_error: 'Falta el costo neto' })
    .int('El costo neto es un entero de pesos, sin decimales')
    .positive('El costo neto debe ser mayor que cero')
    .max(2_000_000_000, 'El costo neto es demasiado grande');
/**
 * Margen como fracción: 0,22 es 22 %. El tope de 5 es un cortafuegos contra un
 * dedo de más —un 500 % de margen es un error de tipeo, no una decisión—.
 */
export const esquemaMargen = z
    .number()
    .min(0, 'El margen no puede ser negativo')
    .max(5, 'Un margen de más de 500 % es un error de tipeo');
// ─── Listados ───────────────────────────────────────────────────────────────
/**
 * `proveedorId` en la consulta, no en el cuerpo: es el proveedor por cuenta de
 * quien opera el equipo. Un proveedor que ponga el de otro recibe 404.
 */
export const esquemaConsultaProveedor = esquemaConsultaPagina.extend({
    proveedorId: z.string().trim().length(36, 'El proveedor no es válido').optional(),
});
// ─── Publicación y edición ──────────────────────────────────────────────────
export const esquemaPublicarProducto = z
    .object({
    sku: z
        .string({ required_error: 'Falta el SKU' })
        .trim()
        .min(1, 'Falta el SKU')
        .max(LARGO_MAXIMO_SKU, 'El SKU es demasiado largo'),
    nombre: z.string({ required_error: 'Falta el nombre' }).trim().min(3, 'El nombre es demasiado corto').max(240),
    descripcion: z.string().trim().max(20_000).default(''),
    marca: z.string({ required_error: 'Falta la marca' }).trim().min(1, 'Falta la marca').max(120),
    mpn: z.string().trim().max(120).nullish(),
    marcaFabricante: z.string().trim().max(120).nullish(),
    categoriaId: z.string({ required_error: 'Falta la categoría' }).trim().length(36, 'La categoría no es válida'),
    costoNeto,
    pesoKg,
    largoCm,
    anchoCm,
    altoCm,
    /**
     * Stock inicial. Opcional, y cuando viene NO se escribe la columna: se
     * lleva el saldo a ese valor con un movimiento en el libro.
     */
    stock: z.number().int('El stock es un entero de unidades').min(0).max(10_000_000).optional(),
    activo: z.boolean().optional(),
})
    .strict();
/**
 * Edición. Todo opcional —es un PATCH—, y `margenOverride` aparece acá pero el
 * módulo lo rechaza si quien lo manda no es del equipo: el proveedor no fija su
 * propio precio de venta.
 *
 * Lo que NO es editable por este camino: el SKU (es la identidad del producto y
 * la clave de la importación), el proveedor dueño y los saldos de stock, que se
 * mueven solo por el libro de movimientos.
 */
export const esquemaEditarProducto = z
    .object({
    nombre: z.string().trim().min(3, 'El nombre es demasiado corto').max(240).optional(),
    descripcion: z.string().trim().max(20_000).optional(),
    marca: z.string().trim().min(1).max(120).optional(),
    mpn: z.string().trim().max(120).nullish(),
    marcaFabricante: z.string().trim().max(120).nullish(),
    categoriaId: z.string().trim().length(36, 'La categoría no es válida').optional(),
    costoNeto: costoNeto.optional(),
    pesoKg: pesoKg.optional(),
    largoCm: largoCm.optional(),
    anchoCm: anchoCm.optional(),
    altoCm: altoCm.optional(),
    activo: z.boolean().optional(),
    /** Solo del equipo. Un proveedor que lo mande recibe 400. */
    margenOverride: esquemaMargen.nullish(),
})
    .strict();
// ─── Movimientos de stock ───────────────────────────────────────────────────
/**
 * Los ÚNICOS tipos que un proveedor puede emitir.
 *
 * `reserva`, `liberacion` y `salida` las emite el sistema al mover las órdenes.
 * Dejar que un proveedor inserte una `liberacion` permitiría soltar las
 * reservas de órdenes vivas de clientes y vender dos veces la misma unidad: fue
 * un agujero real de la v1, donde la política de inserción de movimientos no
 * restringía el tipo.
 */
export const TIPOS_MOVIMIENTO_PROVEEDOR = ['ingreso', 'ajuste'];
const MENSAJE_TIPO = 'Solo se aceptan movimientos de tipo "ingreso" o "ajuste": las reservas, ' +
    'liberaciones y salidas las emite el sistema al mover las órdenes.';
export const esquemaMovimientoProveedor = z
    .object({
    productoId: z.string({ required_error: 'Falta el producto' }).trim().min(1, 'Falta el producto'),
    /*
     * `errorMap` a secas, sin `required_error` ni `invalid_type_error`: Zod
     * lanza si se combinan. Un solo mapa cubre los tres casos que importan —
     * ausente, de otro tipo, y el que de verdad hay que explicar: un tipo
     * válido del enum de la base pero prohibido acá, como `liberacion`.
     */
    tipo: z.enum(TIPOS_MOVIMIENTO_PROVEEDOR, { errorMap: () => ({ message: MENSAJE_TIPO }) }),
    /**
     * Magnitud POSITIVA para `ingreso`; con signo para `ajuste`, porque
     * corregir un inventario puede ser hacia arriba o hacia abajo. Cero no
     * significa nada y se rechaza.
     */
    cantidad: z
        .number({ required_error: 'Falta la cantidad' })
        .int('La cantidad es un entero de unidades')
        .refine((n) => n !== 0, 'La cantidad no puede ser cero')
        .refine((n) => Math.abs(n) <= 10_000_000, 'La cantidad es demasiado grande'),
    nota: z.string().trim().max(500).optional(),
})
    .strict();
// ─── Importación masiva ─────────────────────────────────────────────────────
/**
 * Una fila de catálogo tal como la deja el parser de CSV del dominio.
 *
 * La forma es la de `FilaCatalogo` de `@compranorte/domain`, replicada acá
 * porque `contracts` no depende del dominio. El parser corre en el NAVEGADOR
 * para dar respuesta inmediata sobre un archivo de miles de filas; este esquema
 * es la revalidación del servidor, que no confía en lo que llegó parseado.
 *
 * Los nombres van en snake_case a propósito: son los del archivo CSV, y
 * traducirlos en el camino solo agregaría un lugar donde equivocarse.
 */
export const esquemaFilaCatalogo = z
    .object({
    sku: z.string().trim().min(1, 'Falta el SKU').max(LARGO_MAXIMO_SKU),
    nombre: z.string().trim().min(1, 'Falta el nombre').max(240),
    descripcion: z.string().trim().max(20_000).default(''),
    marca: z.string().trim().min(1, 'Falta la marca').max(120),
    mpn: z.string().trim().max(120).default(''),
    marca_fabricante: z.string().trim().max(120).default(''),
    /** Slug o nombre de la categoría, indistintamente y sin distinguir mayúsculas. */
    categoria: z.string().trim().min(1, 'Falta la categoría').max(120),
    costo_neto: costoNeto,
    /** `null` significa "no tocar el stock", que no es lo mismo que cero. */
    stock: z.number().int().min(0).max(10_000_000).nullable().default(null),
    peso_kg: pesoKg,
    largo_cm: largoCm,
    ancho_cm: anchoCm,
    alto_cm: altoCm,
})
    .strict();
export const esquemaImportarCatalogo = z
    .object({
    /** Solo lo usa el equipo, y pasa igual por `proveedorObjetivo`. */
    proveedorId: z.string().trim().length(36, 'El proveedor no es válido').optional(),
    filas: z
        .array(esquemaFilaCatalogo)
        .min(1, 'El archivo no tiene filas válidas')
        .max(TOPE_FILAS_IMPORTACION, `La carga supera el tope de ${TOPE_FILAS_IMPORTACION} filas`),
})
    .strict();
//# sourceMappingURL=proveedor.js.map